import React from 'react';

import { Select } from '@aline/common';
import { link, routes } from '@aline/routes';
import { Box, Chip, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { GridCellParams } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import {
  documentListButtonId,
  QuickViewButton,
  RelationshipCountChip,
  RelationshipTypeSelect,
  Spacer,
  StorableIcon,
} from 'src/components';
import { getRelationshipLabel } from 'src/components/generic/SelectRelatedDocumentsWithRelationshipDialog';
import { FavoriteStarIcon } from 'src/components/icons/FavoriteStarIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import type { ReportGridRow, ReportRow } from 'src/components/reports/types';
import { Apollo } from 'src/gql/generated';
import { openLinkFromAnchorClick } from 'src/helpers';
import { createClampStyles } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import { OrphanRelationshipRowCell } from './OrphanRelationshipRowCell';
import { RelationshipActionButtons } from './RelationshipActionButtons';
import {
  getIsSelfReferencingRelationship,
  getRelationshipBadgeTooltip,
  useRelationshipRowTitleClick,
} from './repositoryDocumentNameCellHelpers';
import { UrlRelationshipRowCell } from './UrlRelationshipRowCell';
import { useRelationshipNameCellState } from './useRelationshipNameCellState';

const useNameStyles = makeStyles()(theme => ({
  cell: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap' as const,
    gap: theme.spacing(0.5),
    overflow: 'hidden',
    width: '100%',
  },
  directionLabel: {
    '& .MuiChip-label': {
      alignItems: 'center',
      color: theme.appColors.relationshipDirectionChipText,
      display: 'flex',
      fontFamily: theme.appTypography.buttonSmall.fontFamily,
      fontSize: 12,
      fontWeight: 700,
      height: '100%',
      paddingLeft: 4,
      paddingRight: 4,
      textTransform: 'uppercase' as const,
    },
    '&.MuiChip-root': {
      alignItems: 'center',
      backgroundColor: theme.appColors.relationshipDirectionChipBackground,
      borderRadius: 4,
      boxSizing: 'border-box' as const,
      display: 'flex',
      opacity: 1,
      width: 'fit-content',
    },
  },
  documentName: {
    color: theme.appColors.iconsType,
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 700,
  },
  documentNameClamp2: {
    ...createClampStyles(2),
    flex: 1,
    minWidth: 0,
  },
  documentNameLink: {
    minWidth: 0,
    textDecoration: 'none',
  },
  documentNamePlaceholder: {
    opacity: 0.6,
  },
  matchingDocumentPlaceholderIcon: {
    display: 'block',
    flexShrink: 0,
  },
  nameContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1),
    overflow: 'hidden',
  },
  orphanWarningText: {
    color: IconColors.WarnAmber,
    fontFamily: theme.typography.fontFamily,
    fontSize: 14,
    fontWeight: 700,
  },
  relationshipActions: {
    '& .MuiIconButton-root': {
      padding: theme.spacing(0.75),
    },
  },
  relationshipLabel: {
    color: theme.palette.text.secondary,
    fontFamily: theme.typography.button.fontFamily,
    fontSize: theme.typography.button.fontSize,
    fontWeight: theme.typography.button.fontWeight,
    textTransform: 'none' as const,
  },
}));

type NameCellClasses = ReturnType<typeof useNameStyles>['classes'];

// ---------------------------------------------------------------------------
// DocumentRow-specific rendering
// ---------------------------------------------------------------------------

interface DocumentRowNameCellProps {
  classes: NameCellClasses;
  row: ReportGridRow;
  value: string | null;
}

const DocumentRowNameCell = React.memo(function DocumentRowNameCell({
  classes,
  row,
  value,
}: DocumentRowNameCellProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const currentMatch = matchPath(routes.RepositoryDocumentPreview, location.pathname);
  const isCurrentDocumentOpen = currentMatch?.params?.documentId === row.documentId;

  const handleQuickViewClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (isCurrentDocumentOpen) {
      // Close the drawer by navigating back to repository
      navigate(link.RepositoryDocuments(), { replace: true });
    } else {
      // Open the drawer for this document
      navigate(link.RepositoryDocumentPreview({ documentId: row.documentId }), { replace: true });
    }
  };

  const {
    actions,
    displayedDocumentName,
    isLookingFoMatch,
    isParentDocument,
    isRelationshipExtractionInProgress,
    missingMatchesOnly,
    relationship,
    showRelatedCount,
    showRelationshipLabel,
    unconfirmedRelationshipsOnly,
  } = useRelationshipNameCellState({
    row,
    value,
  });

  /**
   * A self-referencing (equal-ID) relationship models a bound-in agreement — the row's
   * document IS the container, so its own `sourceAgreementName` (the bound-in agreement's
   * name) takes over the title, plus an "Internal Reference" chip. Read straight off the
   * row's relationship rather than the cache-synced `relationship` above: the cache
   * fragment only carries review-status fields, not `sourceAgreementName`.
   */
  const isInternalReference = Boolean(
    row.relationship && getIsSelfReferencingRelationship(row.relationship)
  );
  const internalReferenceTitle = row.relationship?.sourceAgreementName?.trim();
  const titleText =
    isInternalReference && internalReferenceTitle ? internalReferenceTitle : displayedDocumentName;
  const relationshipId = row.relationship?.id ?? null;
  const documentHref = link.DocumentDetail({ documentId: row.documentId });
  const handleRelationshipTitleClick = useRelationshipRowTitleClick({
    documentId: row.documentId,
    llmReasonsMarkdown: row.relationship?.llmReasonsMarkdown,
    relationshipId,
  });

  return (
    <>
      <div className={classes.cell}>
        <div className={classes.nameContainer}>
          <Link
            className={classes.documentNameLink}
            to={documentHref}
            onClick={event => {
              event.stopPropagation();
              /**
               * A relationship row's title points at the relationship card rather than the
               * document, so let that handler take the click when it claims it.
               */
              if (handleRelationshipTitleClick()) {
                event.preventDefault();
                return;
              }
              // Otherwise match the row: open the document in an Aline tab.
              openLinkFromAnchorClick(event, documentHref, { title: titleText });
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1,
              }}
            >
              {!isLookingFoMatch && <StorableIcon storable={row} />}
              {isLookingFoMatch ? (
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 0.5,
                    minWidth: 0,
                  }}
                >
                  <PulseIcon
                    className={classes.matchingDocumentPlaceholderIcon}
                    variant={PulseIconVariant.LoadingPrimary}
                  />
                  <Typography
                    noWrap
                    className={clsx(classes.documentName, classes.documentNamePlaceholder)}
                    variant="h6"
                  >
                    {displayedDocumentName}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  className={clsx(classes.documentName, classes.documentNameClamp2)}
                  variant="h6"
                >
                  {titleText}
                </Typography>
              )}
            </Box>
          </Link>
          {showRelationshipLabel && (
            <Box
              className={classes.relationshipActions}
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 0.5,
              }}
            >
              <Chip
                className={classes.directionLabel}
                data-cy={Select.relationships.directionBadge}
                label={isInternalReference ? 'INTERNAL' : isParentDocument ? 'PARENT' : 'CHILD'}
                size="small"
              />
              <RelationshipTypeSelect
                disableUnderline
                data-cy={Select.relationships.typeSelect}
                getLabel={type =>
                  getRelationshipLabel({
                    direction:
                      row.relationshipDirection === Apollo.RelationshipDirection.Parent
                        ? 'parent'
                        : 'child',
                    type,
                  })
                }
                menuTitle={`Select the relationship type`}
                searchPlaceholder="Search for a type..."
                size="small"
                value={relationship?.childRelationshipType ?? ''}
                variant="standard"
                onChange={async (event: SelectChangeEvent<Apollo.ChildRelationshipType | ''>) => {
                  event.stopPropagation();
                  const newType = event.target.value;
                  if (!newType) return;

                  await actions.handleUpdateRelationshipType(newType);
                }}
              />
              {!isLookingFoMatch && (
                <RelationshipActionButtons confirmTooltip="Confirm relationship" row={row} />
              )}
            </Box>
          )}
        </div>
        <Spacer />
        {showRelatedCount && (
          <RelationshipCountChip
            count={row.childRowCount}
            dataCy={Select.relationships.relatedCountBadge}
            isLoading={isRelationshipExtractionInProgress}
            tooltip={getRelationshipBadgeTooltip({
              count: row.childRowCount,
              isExtracting: isRelationshipExtractionInProgress,
              missingMatchesOnly,
              unconfirmedRelationshipsOnly,
            })}
          />
        )}
        {row.requesterFavorited && <FavoriteStarIcon fill={IconColors.StarYellow} />}
        <QuickViewButton
          data-cy={Select.repository.quickViewDocument}
          id={documentListButtonId}
          tooltipMsg="Quick view document details"
          onClick={e => {
            e.stopPropagation();
            handleQuickViewClick(e);
          }}
        />
      </div>
    </>
  );
});

// ---------------------------------------------------------------------------
// Top-level cell renderer (dispatches by row type)
// ---------------------------------------------------------------------------

export function RepositoryDocumentNameCell({
  row,
  value,
}: GridCellParams<ReportRow, string | null>): JSX.Element {
  const { classes } = useNameStyles();

  switch (row.__typename) {
    case 'GroupHeaderRow':
      return <div className={classes.cell} />;
    case 'OrphanRelationshipRow':
      return <OrphanRelationshipRowCell classes={classes} row={row} />;
    case 'UrlRelationshipRow':
      return <UrlRelationshipRowCell classes={classes} row={row} />;
    case 'Document':
      return <DocumentRowNameCell classes={classes} row={row} value={value ?? null} />;
  }
}
